import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Loader2, ArrowLeft, Send, Play, Pause, Ban, Download,
  ShieldAlert, RefreshCcw, AlertTriangle, ChevronDown, Trash2,
} from "lucide-react";
import { PageHeader, FormRow, KV } from "@/pages/Accounts";
import HardDeleteDialog from "@/components/HardDeleteDialog";

// ---------------------- Campaigns list ----------------------
export function CampaignsListPage() {
  const { id: projectId } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState(null);
  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/campaigns`);
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { load(); }, [projectId]);

  return (
    <div className="p-8 space-y-6">
      <div>
        <Button variant="ghost" size="sm" asChild data-testid="camp-back">
          <Link to={`/projects/${projectId}`}>
            <ArrowLeft className="mr-2 h-4 w-4" />Back to project
          </Link>
        </Button>
      </div>
      <PageHeader
        title="Campaigns"
        description="Filter your audience, pick a template, send DLT-compliant SMS."
        action={
          <Button asChild data-testid="new-campaign-btn">
            <Link to={`/projects/${projectId}/campaigns/new`}>
              <Plus className="mr-2 h-4 w-4" />New campaign
            </Link>
          </Button>
        }
      />
      <Card data-testid="campaigns-list-card">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Recipients</TableHead>
                <TableHead>Sent / Failed</TableHead>
                <TableHead>Created</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items === null && (<TableRow><TableCell colSpan={6} className="text-center py-8"><Loader2 className="inline h-4 w-4 animate-spin" /></TableCell></TableRow>)}
              {items?.length === 0 && (<TableRow><TableCell colSpan={6} className="text-center py-8 text-xs text-muted-foreground">No campaigns yet.</TableCell></TableRow>)}
              {items?.map((c) => (
                <TableRow key={c.id} data-testid={`camp-row-${c.id}`}>
                  <TableCell className="font-medium">
                    <Link to={`/campaigns/${c.id}`}
                          className="hover:underline text-primary"
                          data-testid={`camp-name-link-${c.id}`}>
                      {c.name}
                    </Link>
                  </TableCell>
                  <TableCell><StatusBadge status={c.status} /></TableCell>
                  <TableCell className="text-xs">{c.audience_stats?.final_recipients ?? 0}</TableCell>
                  <TableCell className="text-xs">
                    <span className="font-mono">{c.progress?.numbers_sent ?? 0}</span> / <span className="font-mono">{c.progress?.numbers_failed ?? 0}</span>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{String(c.created_at).slice(0, 16).replace("T", " ")}</TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button variant="ghost" size="sm" asChild data-testid={`camp-open-${c.id}`}>
                      <Link to={`/campaigns/${c.id}`}>Open</Link>
                    </Button>
                    {isAdmin && (
                      <HardDeleteDialog
                        resourceType="campaign"
                        resourceName={c.name}
                        resourceId={c.id}
                        endpoint="/campaigns"
                        onDeleted={load}
                        testId={`camp-harddel-${c.id}`}
                        trigger={
                          <Button variant="ghost" size="icon"
                            data-testid={`camp-harddel-${c.id}-btn`}
                            title="Delete permanently">
                            <Trash2 className="h-3 w-3 text-destructive" />
                          </Button>
                        }
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    draft: "outline", queued: "secondary", running: "default",
    paused: "secondary", completed: "default", cancelled: "destructive",
    failed: "destructive",
  };
  return <Badge variant={map[status] || "outline"} data-testid={`camp-status-${status}`}>{status}</Badge>;
}

// ---------------------- Campaign builder ----------------------
export function CampaignBuilderPage() {
  const { id: projectId } = useParams();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const preSegId = sp.get("segment_id") || "";
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [contactLists, setContactLists] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [segments, setSegments] = useState([]);
  const [proj, setProj] = useState(null);
  const [form, setForm] = useState({
    name: "", description: "", contact_list_id: "",
    segment_id: preSegId, template_id: "",
    variable_mapping: {}, batch_size: 300, delay_seconds: 1,
    max_retries: 2, duplicate_suppression_days: 0,
    consent_scope_hint: "", skip_consent_scrub: false,
    skip_consent_reason: "",
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [p, cls] = await Promise.all([
          api.get(`/projects/${projectId}`),
          api.get(`/projects/${projectId}/contact-lists`),
        ]);
        setProj(p.data);
        setContactLists(cls.data);
        const [tpls] = await Promise.all([
          api.get(`/accounts/${p.data.account_id}/templates?status=active`).catch(() => ({ data: [] })),
        ]);
        setTemplates(tpls.data || []);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    };
    load();
  }, [projectId]);

  useEffect(() => {
    if (!form.contact_list_id) return setSegments([]);
    api.get(`/contact-lists/${form.contact_list_id}/segments`)
       .then(r => setSegments(r.data))
       .catch(() => setSegments([]));
  }, [form.contact_list_id]);

  const selectedTpl = templates.find(t => t.id === form.template_id);

  const create = async (thenSend = false) => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.segment_id) delete payload.segment_id;
      if (!payload.consent_scope_hint) delete payload.consent_scope_hint;
      payload.batch_size = Number(payload.batch_size);
      payload.delay_seconds = Number(payload.delay_seconds);
      payload.max_retries = Number(payload.max_retries);
      payload.duplicate_suppression_days = Number(payload.duplicate_suppression_days);
      // Only send skip_consent_scrub if admin AND explicitly toggled
      if (!isAdmin || !payload.skip_consent_scrub) {
        delete payload.skip_consent_scrub;
      }
      const reason = payload.skip_consent_reason;
      delete payload.skip_consent_reason;
      const { data } = await api.post(`/projects/${projectId}/campaigns`, payload);
      // If admin flipped skip-consent, apply the reason via the dedicated endpoint (persists audit log)
      if (isAdmin && payload.skip_consent_scrub && reason) {
        try {
          await api.post(`/campaigns/${data.id}/set-skip-consent`, {
            skip_consent_scrub: true, reason,
          });
        } catch (e) {
          toast.warning("Draft created but skip-consent audit entry failed: " + formatApiError(e));
        }
      }
      setPreview(data);
      toast.success(`Draft created. Final recipients: ${data.audience_stats.final_recipients}`);
      if (thenSend) {
        await api.post(`/campaigns/${data.id}/send`);
        toast.success("Campaign started");
        nav(`/campaigns/${data.id}`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-8 space-y-6 max-w-4xl">
      <div>
        <Button variant="ghost" size="sm" asChild>
          <Link to={`/projects/${projectId}/campaigns`}>
            <ArrowLeft className="mr-2 h-4 w-4" />Back to campaigns
          </Link>
        </Button>
      </div>
      <PageHeader title="New campaign" description={proj?.name} />

      <Card><CardHeader><CardTitle className="text-base">1. Basics</CardTitle></CardHeader><CardContent className="space-y-3">
        <FormRow label="Name">
          <Input data-testid="camp-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
        </FormRow>
        <FormRow label="Description">
          <Textarea rows={2} data-testid="camp-desc" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
        </FormRow>
      </CardContent></Card>

      <Card><CardHeader><CardTitle className="text-base">2. Audience</CardTitle></CardHeader><CardContent className="space-y-3">
        <FormRow label="Contact list">
          <Select value={form.contact_list_id} onValueChange={v => setForm({ ...form, contact_list_id: v, segment_id: "" })}>
            <SelectTrigger data-testid="camp-cl-select"><SelectValue placeholder="Pick a contact list" /></SelectTrigger>
            <SelectContent>
              {contactLists.map(c => (<SelectItem key={c.id} value={c.id}>{c.name} ({c.row_stats.valid_rows} valid)</SelectItem>))}
            </SelectContent>
          </Select>
        </FormRow>
        <FormRow label="Segment (optional — uses filters if empty)">
          <Select value={form.segment_id} onValueChange={v => setForm({ ...form, segment_id: v })}>
            <SelectTrigger data-testid="camp-seg-select"><SelectValue placeholder="No segment (use full list)" /></SelectTrigger>
            <SelectContent>
              {segments.map(s => (<SelectItem key={s.id} value={s.id}>{s.name} (~{s.estimated_count})</SelectItem>))}
            </SelectContent>
          </Select>
        </FormRow>
      </CardContent></Card>

      <Card><CardHeader><CardTitle className="text-base">3. Template & variables</CardTitle></CardHeader><CardContent className="space-y-3">
        <FormRow label="Template">
          <Select value={form.template_id} onValueChange={v => setForm({ ...form, template_id: v, variable_mapping: {} })}>
            <SelectTrigger data-testid="camp-tpl-select"><SelectValue placeholder="Pick a DLT template" /></SelectTrigger>
            <SelectContent>
              {templates.map(t => (
                <SelectItem key={t.id} value={t.id}>
                  [{t.category}] {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormRow>
        {selectedTpl && (
          <>
            <div className="text-xs text-muted-foreground bg-muted p-3 rounded font-mono whitespace-pre-wrap">
              {selectedTpl.body}
            </div>
            {selectedTpl.variables?.length > 0 && (
              <div className="space-y-2">
                <div className="text-xs font-medium">Variable mapping</div>
                {selectedTpl.variables.map((v, i) => (
                  <div key={i} className="grid grid-cols-3 gap-2 items-center">
                    <div className="font-mono text-xs">#{i} {v.placeholder}</div>
                    <Input
                      className="text-xs"
                      placeholder="column name OR static:xxx"
                      data-testid={`camp-varmap-${i}`}
                      value={form.variable_mapping[String(i)] || ""}
                      onChange={e => setForm({ ...form, variable_mapping: { ...form.variable_mapping, [String(i)]: e.target.value } })}
                    />
                    <div className="text-[10px] text-muted-foreground">kind: {v.kind}</div>
                  </div>
                ))}
              </div>
            )}
            {selectedTpl.category === "service_explicit" && (
              <FormRow label="Consent scope hint (substring match against consent_records.scope)">
                <Input data-testid="camp-consent-hint" value={form.consent_scope_hint} onChange={e => setForm({ ...form, consent_scope_hint: e.target.value })} placeholder="e.g. engineering" />
              </FormRow>
            )}
            {selectedTpl.category === "service_explicit" && isAdmin && (
              <Alert variant={form.skip_consent_scrub ? "destructive" : "default"} data-testid="camp-skipconsent-alert">
                <ShieldAlert className="h-4 w-4" />
                <AlertTitle className="text-sm flex items-center justify-between">
                  <span>Skip consent scrub (admin only)</span>
                  <Switch
                    checked={form.skip_consent_scrub}
                    onCheckedChange={(v) => setForm({ ...form, skip_consent_scrub: v })}
                    data-testid="camp-skipconsent-switch"
                  />
                </AlertTitle>
                <AlertDescription className="text-xs space-y-2">
                  <div>
                    When ON, service_explicit consent checks are <b>bypassed</b> — every eligible recipient will receive the SMS regardless of consent records. Use only if you have <b>proof of consent</b> outside SMSCraft. This action is logged in the audit trail for TRAI compliance.
                  </div>
                  {form.skip_consent_scrub && (
                    <div className="pt-2">
                      <Label className="text-xs">Reason (required — audit record)</Label>
                      <Textarea
                        rows={2}
                        data-testid="camp-skipconsent-reason"
                        value={form.skip_consent_reason}
                        onChange={(e) => setForm({ ...form, skip_consent_reason: e.target.value })}
                        placeholder="e.g. Consent captured via 2026-01 offline enrolment forms; scanned copies on file at admissioncrafts.com"
                      />
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            )}
          </>
        )}
      </CardContent></Card>

      <Card><CardHeader className="cursor-pointer" onClick={() => setShowAdvanced(v => !v)}>
        <CardTitle className="text-base flex items-center justify-between">
          <span>4. Send options</span>
          <div className="flex items-center gap-2 text-xs text-muted-foreground font-normal">
            <Badge variant="outline" className="text-[10px]" data-testid="camp-adv-summary">
              batch {form.batch_size} · delay {form.delay_seconds}s · retries {form.max_retries} · dup-skip {form.duplicate_suppression_days}d
            </Badge>
            <ChevronDown className={`h-4 w-4 transition-transform ${showAdvanced ? "rotate-180" : ""}`} />
          </div>
        </CardTitle>
      </CardHeader>
      {showAdvanced && (
        <CardContent className="grid grid-cols-2 gap-3">
          <FormRow label="Batch size" hint="max 300 per KAPSYSTEM spec"><Input type="number" min="1" max="300" data-testid="camp-batch" value={form.batch_size} onChange={e => setForm({ ...form, batch_size: e.target.value })} /></FormRow>
          <FormRow label="Delay (seconds)"><Input type="number" min="0" step="0.5" data-testid="camp-delay" value={form.delay_seconds} onChange={e => setForm({ ...form, delay_seconds: e.target.value })} /></FormRow>
          <FormRow label="Max retries"><Input type="number" min="0" max="5" data-testid="camp-retries" value={form.max_retries} onChange={e => setForm({ ...form, max_retries: e.target.value })} /></FormRow>
          <FormRow label="Dup-suppress last N days"><Input type="number" min="0" data-testid="camp-dupdays" value={form.duplicate_suppression_days} onChange={e => setForm({ ...form, duplicate_suppression_days: e.target.value })} /></FormRow>
        </CardContent>
      )}
      </Card>

      {preview && (
        <Card data-testid="camp-preview-card"><CardHeader><CardTitle className="text-base">5. Preview & Estimate</CardTitle></CardHeader><CardContent className="text-sm space-y-2">
          <KV k="Matched" v={preview.audience_stats.matched} />
          <KV k="DND blocked" v={preview.audience_stats.dnd_blocked} />
          <KV k="Consent blocked" v={preview.audience_stats.consent_blocked} />
          <KV k="Dup suppressed" v={preview.audience_stats.duplicate_suppressed} />
          <KV k="Final recipients" v={<b>{preview.audience_stats.final_recipients}</b>} />
          <KV k="Cost estimate" v={`₹${(preview.cost_estimate.cost_paise / 100).toFixed(2)} (${preview.cost_estimate.sms_count} SMS × ${preview.cost_estimate.segments_per_message} seg)`} />
        </CardContent></Card>
      )}

      <div className="flex gap-2">
        <Button
          variant="outline"
          onClick={() => create(false)}
          disabled={
            busy || !form.name || !form.contact_list_id || !form.template_id ||
            (form.skip_consent_scrub && !form.skip_consent_reason?.trim())
          }
          data-testid="camp-preview-btn"
        >
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Preview / Create draft
        </Button>
        {preview && (
          <Button variant="destructive" onClick={() => api.post(`/campaigns/${preview.id}/send`).then(() => { toast.success("Started"); nav(`/campaigns/${preview.id}`); }).catch(e => toast.error(formatApiError(e)))} data-testid="camp-send-btn">
            <Send className="mr-2 h-4 w-4" />Send campaign ({preview.audience_stats.final_recipients})
          </Button>
        )}
      </div>

      {preview && (
        <RecurringSection campaignId={preview.id} onDone={() => nav(`/projects/${projectId}/scheduled`)} />
      )}
    </div>
  );
}

// ---------------------- Recurring section ----------------------
function RecurringSection({ campaignId, onDone }) {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState({
    interval: "weekly",
    days_of_week: [0], // Mon
    day_of_month: 1,
    time_of_day: "10:00",
    tz: "Asia/Kolkata",
    end_at: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        interval: cfg.interval,
        time_of_day: cfg.time_of_day,
        tz: cfg.tz,
      };
      if (cfg.interval === "weekly") payload.days_of_week = cfg.days_of_week;
      if (cfg.interval === "monthly") payload.day_of_month = Number(cfg.day_of_month);
      if (cfg.end_at) payload.end_at = new Date(cfg.end_at).toISOString();
      await api.post(`/campaigns/${campaignId}/make-recurring`, payload);
      toast.success("Campaign converted to a recurring template");
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleDay = (d) => {
    setCfg({
      ...cfg,
      days_of_week: cfg.days_of_week.includes(d)
        ? cfg.days_of_week.filter(x => x !== d)
        : [...cfg.days_of_week, d].sort(),
    });
  };

  const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  return (
    <Card data-testid="recur-section">
      <CardHeader className="cursor-pointer" onClick={() => setOpen(!open)}>
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2">
            <RefreshCcw className="h-4 w-4" /> Make this campaign recurring
          </span>
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </CardTitle>
        <CardDescription>
          Convert this draft into a template that re-sends on a schedule. Each run picks up fresh contact list state.
        </CardDescription>
      </CardHeader>
      {open && (
        <CardContent className="space-y-3">
          <FormRow label="Interval">
            <Select value={cfg.interval} onValueChange={(v) => setCfg({ ...cfg, interval: v })}>
              <SelectTrigger data-testid="recur-interval-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </FormRow>

          {cfg.interval === "weekly" && (
            <FormRow label="Days of week (Mon..Sun)">
              <div className="flex flex-wrap gap-2">
                {DAYS.map((d, i) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => toggleDay(i)}
                    data-testid={`recur-dow-${i}`}
                    className={`px-3 py-1 rounded-md text-xs border transition ${cfg.days_of_week.includes(i) ? "bg-primary text-primary-foreground border-primary" : "border-border bg-background hover:bg-muted"}`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </FormRow>
          )}

          {cfg.interval === "monthly" && (
            <FormRow label="Day of month (1..28)">
              <Input
                type="number" min="1" max="28"
                value={cfg.day_of_month}
                onChange={(e) => setCfg({ ...cfg, day_of_month: e.target.value })}
                data-testid="recur-dom"
              />
            </FormRow>
          )}

          <div className="grid grid-cols-2 gap-3">
            <FormRow label="Time of day (24h)">
              <Input
                type="time"
                value={cfg.time_of_day}
                onChange={(e) => setCfg({ ...cfg, time_of_day: e.target.value })}
                data-testid="recur-time"
              />
            </FormRow>
            <FormRow label="Time zone">
              <Input value={cfg.tz} onChange={(e) => setCfg({ ...cfg, tz: e.target.value })} data-testid="recur-tz" />
            </FormRow>
          </div>

          <FormRow label="End at (optional, UTC)">
            <Input
              type="datetime-local"
              value={cfg.end_at}
              onChange={(e) => setCfg({ ...cfg, end_at: e.target.value })}
              data-testid="recur-end"
            />
          </FormRow>

          <Button
            onClick={submit}
            disabled={busy || (cfg.interval === "weekly" && cfg.days_of_week.length === 0)}
            data-testid="recur-submit"
          >
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Enable recurring
          </Button>
        </CardContent>
      )}
    </Card>
  );
}

// ---------------------- End recurring ----------------------

// ---------------------- Campaign detail ----------------------
export function CampaignDetailPage() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [msgs, setMsgs] = useState(null);
  const [testMobile, setTestMobile] = useState("");
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/campaigns/${id}`);
      setC(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const loadMsgs = async () => {
    try {
      const { data } = await api.get(`/campaigns/${id}/messages?per_page=100`);
      setMsgs(data.items);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); loadMsgs(); }, [id]);
  useEffect(() => {
    if (!c || !["running", "queued"].includes(c.status)) return;
    const iv = setInterval(() => { load(); loadMsgs(); }, 2000);
    return () => clearInterval(iv);
  }, [c?.status]);

  const act = async (action) => {
    try {
      await api.post(`/campaigns/${id}/${action}`);
      toast.success(action);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doTest = async () => {
    try {
      const { data } = await api.post(`/campaigns/${id}/test-send`,
        { mobile_numbers: testMobile.split(/[,\s]+/).filter(Boolean).slice(0, 5), override_variables: { "0": "TEST" } });
      setTestResult(data);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const exportCsv = async () => {
    try {
      const r = await api.get(`/campaigns/${id}/messages/export`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `${c.name}_log.csv`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!c) return <div className="p-8"><Loader2 className="h-4 w-4 animate-spin" /></div>;

  return (
    <div className="p-8 space-y-6">
      <div>
        <Button variant="ghost" size="sm" asChild>
          <Link to={`/projects/${c.project_id}/campaigns`}>
            <ArrowLeft className="mr-2 h-4 w-4" />Back
          </Link>
        </Button>
      </div>
      <PageHeader
        title={c.name}
        description={<span className="font-mono text-xs">Status: <StatusBadge status={c.status} /> · {c.audience_stats.final_recipients} recipients</span>}
        action={
          <div className="flex gap-2">
            {c.status === "draft" && (<Button onClick={() => act("send")} data-testid="det-send"><Send className="mr-2 h-4 w-4" />Send</Button>)}
            {c.status === "running" && (<Button variant="outline" onClick={() => act("pause")} data-testid="det-pause"><Pause className="mr-2 h-4 w-4" />Pause</Button>)}
            {c.status === "paused" && (<Button onClick={() => act("resume")} data-testid="det-resume"><Play className="mr-2 h-4 w-4" />Resume</Button>)}
            {["running", "paused", "queued"].includes(c.status) && (<Button variant="destructive" onClick={() => act("cancel")} data-testid="det-cancel"><Ban className="mr-2 h-4 w-4" />Cancel</Button>)}
          </div>
        }
      />

      <Tabs defaultValue="overview" data-testid="det-tabs">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="recipients">Recipients</TabsTrigger>
          <TabsTrigger value="delivery" data-testid="det-tab-delivery">Delivery report</TabsTrigger>
          <TabsTrigger value="test">Test sends</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-4 space-y-4">
          <Card><CardHeader><CardTitle className="text-base">Progress</CardTitle></CardHeader><CardContent className="space-y-2 text-sm">
            <Progress value={c.progress?.percent || 0} />
            <div className="text-xs font-mono">
              {c.progress?.batches_done}/{c.progress?.total_batches} batches · sent {c.progress?.numbers_sent} · failed {c.progress?.numbers_failed}
            </div>
            {c.progress?.last_error && <div className="text-xs text-destructive">{c.progress.last_error}</div>}
          </CardContent></Card>
          <Card><CardHeader><CardTitle className="text-base">Audience & cost</CardTitle></CardHeader><CardContent className="text-sm">
            <KV k="Matched" v={c.audience_stats?.matched} />
            <KV k="DND blocked" v={c.audience_stats?.dnd_blocked} />
            <KV k="Consent blocked" v={c.audience_stats?.consent_blocked} />
            <KV k="Duplicate suppressed" v={c.audience_stats?.duplicate_suppressed} />
            <KV k="Invalid phones" v={c.audience_stats?.invalid_phones} />
            <KV k="Final recipients" v={<b>{c.audience_stats?.final_recipients}</b>} />
            <KV k="Cost" v={`₹${((c.cost_estimate?.cost_paise || 0) / 100).toFixed(2)}`} />
          </CardContent></Card>
          {c.skip_consent_scrub && (
            <Alert variant="destructive" data-testid="det-skipconsent-notice">
              <ShieldAlert className="h-4 w-4" />
              <AlertTitle>Consent scrub bypassed</AlertTitle>
              <AlertDescription className="text-xs">
                <div>An admin flipped skip_consent_scrub on this campaign. Consent records were NOT checked for eligibility.</div>
                {c.skip_consent_reason && <div className="mt-2"><b>Reason (audit record):</b> <span className="font-mono">{c.skip_consent_reason}</span></div>}
              </AlertDescription>
            </Alert>
          )}
        </TabsContent>
        <TabsContent value="recipients" className="mt-4">
          <Card><CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Messages</CardTitle>
            <Button variant="outline" size="sm" onClick={exportCsv} data-testid="det-export"><Download className="mr-2 h-3 w-3" />CSV</Button>
          </CardHeader><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Phone</TableHead><TableHead>Status</TableHead><TableHead>Msg ID</TableHead><TableHead>Attempt</TableHead><TableHead>Error</TableHead></TableRow></TableHeader>
              <TableBody>
                {msgs?.map(m => (
                  <TableRow key={m.id} data-testid={`det-msg-${m.phone_e164}`}>
                    <TableCell className="font-mono text-xs">{m.phone_e164}</TableCell>
                    <TableCell><Badge variant={m.status === "accepted" ? "default" : m.status === "failed" ? "destructive" : "secondary"} className="text-[10px]">{m.status}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{m.kapsystem_message_id || "—"}</TableCell>
                    <TableCell className="text-xs">{m.attempt}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[240px] truncate">{m.error || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>
        <TabsContent value="delivery" className="mt-4">
          <DeliveryReportTab cid={id} campaign={c} />
        </TabsContent>
        <TabsContent value="test" className="mt-4">
          <Card><CardHeader><CardTitle className="text-base">Test send</CardTitle></CardHeader><CardContent className="space-y-3">
            <div className="flex gap-2">
              <Input placeholder="1-5 phones, comma-separated" data-testid="det-test-phones" value={testMobile} onChange={e => setTestMobile(e.target.value)} />
              <Button onClick={doTest} disabled={!testMobile} data-testid="det-test-btn">Send test</Button>
            </div>
            {testResult && (<pre className="text-xs whitespace-pre-wrap bg-muted p-3 rounded max-h-64 overflow-auto">{JSON.stringify(testResult, null, 2)}</pre>)}
            {c.test_sends?.length > 0 && (<div className="text-xs space-y-1">
              <div className="font-medium">Previous test sends</div>
              {c.test_sends.map((t, i) => (<div key={i} className="font-mono text-[10px] text-muted-foreground">{t.sent_at} → {t.result?.ok ? "ok" : "fail"} ({t.mobile_numbers?.length || 0} nums)</div>))}
            </div>)}
          </CardContent></Card>
        </TabsContent>
        <TabsContent value="settings" className="mt-4">
          <Card><CardHeader><CardTitle className="text-base">Snapshot at creation</CardTitle></CardHeader><CardContent className="text-xs">
            <pre className="whitespace-pre-wrap bg-muted p-3 rounded max-h-96 overflow-auto">{JSON.stringify({
              filters_snapshot: c.filters_snapshot,
              template_snapshot: c.template_snapshot,
              variable_mapping: c.variable_mapping,
              batch_size: c.batch_size, delay_seconds: c.delay_seconds,
              max_retries: c.max_retries,
              duplicate_suppression_days: c.duplicate_suppression_days,
            }, null, 2)}</pre>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}


// ---------------------- Delivery Report Tab ----------------------
function DeliveryReportTab({ cid, campaign }) {
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [polling, setPolling] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/campaigns/${cid}/delivery-report`);
      setReport(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { load(); }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps

  const pollNow = async () => {
    setPolling(true);
    try {
      const { data } = await api.post(`/campaigns/${cid}/poll-dlr-now`);
      toast.success(`Polled ${data.polled} · Resolved ${data.resolved} · Pending ${data.still_pending}`);
      if (data.parser_confidence === "low") {
        toast.warning("Parser confidence LOW — DLR format may have drifted.");
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPolling(false);
    }
  };

  const exportDlr = async (fmt) => {
    setBusy(true);
    try {
      const r = await api.get(`/campaigns/${cid}/delivery-report/export?fmt=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `${campaign?.name || "campaign"}_delivery.${fmt === "html" ? "html" : "csv"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const stats = report?.delivery_stats || campaign?.delivery_stats || {};
  const parserConf = stats.parser_confidence;
  const rate = stats.delivery_rate_percent ?? 0;

  return (
    <div className="space-y-4">
      {parserConf === "low" && (
        <Alert variant="destructive" data-testid="dlr-low-conf-alert">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Parser confidence LOW</AlertTitle>
          <AlertDescription className="text-xs">
            The DLR response format looks unfamiliar — this is our early-warning for vendor drift. Review the account&apos;s DLR config and raw response, or contact KAPSYSTEM support.
          </AlertDescription>
        </Alert>
      )}

      <Card data-testid="dlr-summary-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-base">Delivery summary</CardTitle>
            <CardDescription>
              {stats.last_poll_at ? <>Last polled <span className="font-mono">{String(stats.last_poll_at).slice(0, 19).replace("T", " ")}</span></> : "Not yet polled"}
              {stats.poll_completed_at && <span className="ml-2 text-emerald-600">· polling complete</span>}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={pollNow} disabled={polling} data-testid="dlr-poll-now-btn">
              {polling ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <RefreshCcw className="mr-2 h-3 w-3" />}
              Poll now
            </Button>
            <Button variant="outline" size="sm" onClick={() => exportDlr("csv")} disabled={busy} data-testid="dlr-export-csv"><Download className="mr-2 h-3 w-3" />CSV</Button>
            <Button variant="outline" size="sm" onClick={() => exportDlr("html")} disabled={busy} data-testid="dlr-export-html"><Download className="mr-2 h-3 w-3" />HTML</Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
            <MetricPill label="Delivered" value={stats.delivered || 0} tone="success" testid="dlr-metric-delivered" />
            <MetricPill label="Failed" value={stats.failed || 0} tone="danger" testid="dlr-metric-failed" />
            <MetricPill label="DND" value={stats.dnd || 0} tone="warn" testid="dlr-metric-dnd" />
            <MetricPill label="Expired" value={stats.expired || 0} tone="warn" testid="dlr-metric-expired" />
            <MetricPill label="Rejected" value={stats.rejected || 0} tone="danger" testid="dlr-metric-rejected" />
            <MetricPill label="Unknown" value={stats.unknown || 0} tone="muted" testid="dlr-metric-unknown" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Delivery rate</span>
              <span className="font-mono font-semibold">{rate}%</span>
            </div>
            <Progress value={rate} data-testid="dlr-rate-progress" />
          </div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <Card data-testid="dlr-failure-reasons-card">
          <CardHeader><CardTitle className="text-base">Top failure reasons</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Reason</TableHead><TableHead className="text-right">Count</TableHead></TableRow></TableHeader>
              <TableBody>
                {(report?.top_failure_reasons || []).length === 0 && (
                  <TableRow><TableCell colSpan={2} className="text-center py-6 text-xs text-muted-foreground">No failures.</TableCell></TableRow>
                )}
                {(report?.top_failure_reasons || []).map((r, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-xs font-mono">{r.reason}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{r.count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card data-testid="dlr-latency-card">
          <CardHeader>
            <CardTitle className="text-base">Time to deliver</CardTitle>
            <CardDescription>Percentiles (seconds from attempt → delivered)</CardDescription>
          </CardHeader>
          <CardContent>
            {!report?.time_to_deliver_seconds ? (
              <div className="text-xs text-muted-foreground py-6 text-center">Need ≥10 delivered messages to compute percentiles.</div>
            ) : (
              <div className="space-y-1 text-sm">
                <KV k="p50" v={<span className="font-mono">{report.time_to_deliver_seconds.p50}s</span>} />
                <KV k="p90" v={<span className="font-mono">{report.time_to_deliver_seconds.p90}s</span>} />
                <KV k="p99" v={<span className="font-mono">{report.time_to_deliver_seconds.p99}s</span>} />
                <KV k="samples" v={<span className="font-mono">{report.time_to_deliver_seconds.n}</span>} />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function MetricPill({ label, value, tone = "muted", testid }) {
  const toneMap = {
    success: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
    danger: "bg-destructive/10 text-destructive border-destructive/30",
    warn: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30",
    muted: "bg-muted text-muted-foreground border-border",
  };
  return (
    <div className={`rounded border px-3 py-2 ${toneMap[tone]}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide font-medium">{label}</div>
      <div className="text-lg font-mono tabular-nums font-semibold">{new Intl.NumberFormat("en-IN").format(value)}</div>
    </div>
  );
}
